import { useState, useMemo, type ReactNode } from 'react';
import { type QuizAnswers, type QuestionProps, ProfileFields } from './types';
import {
  findNextVisibleQuestion,
  isQuestionVisible,
  quizAnswersToProfilePayload,
} from './utils';
import { ProfileQuizContext, initialQuizAnswers } from '.';
import { useRecommendationProfile } from '@/context/recommendation-profile';
import {
  ProfileStepIds,
  type RecommendationProfile,
} from '@/models/recommendation';

const extractQuizAnswersFromProfile = (
  profile: RecommendationProfile
): QuizAnswers => ({
  [ProfileFields.CurrentJob]: profile.current_job ?? null,
  [ProfileFields.DesiredJob]: profile.desired_job ?? null,
  [ProfileFields.AddressCity]: profile.address_city ?? null,
  [ProfileFields.AddressState]: profile.address_state ?? null,
  [ProfileFields.Scholarity]: profile.scholarity ?? null,
  [ProfileFields.Goal]: profile.goal ?? null,
  [ProfileFields.CourseTypes]: profile.course_types ?? null,
  [ProfileFields.Languages]: profile.languages ?? null,
  [ProfileFields.NonResident]: profile.non_resident ?? null,
  [ProfileFields.AgreedToReceiveEmails]:
    profile.agreed_to_receive_emails ?? null,
  [ProfileFields.Email]: profile.email ?? null,
  [ProfileFields.Phone]: profile.phone ?? null,
});

interface ProfileQuizProviderProps {
  questions: QuestionProps[];
  profile: RecommendationProfile;
  children: ReactNode;
}

export const ProfileQuizProvider = ({
  questions,
  profile,
  children,
}: ProfileQuizProviderProps) => {
  const questionIndex = questions.findIndex(
    (question) => question.id === profile?.current_step
  );
  const initialQuestionIndex = questionIndex >= 0 ? questionIndex : 0;
  const initialAnswers = profile
    ? extractQuizAnswersFromProfile(profile)
    : initialQuizAnswers;

  const [answers, setAnswers] = useState<QuizAnswers>(initialAnswers);
  const [isQuizComplete, setIsQuizComplete] = useState(false);
  const [isCurrentStepValid, setIsCurrentStepValid] = useState(false);
  const [submittedQuestionId, setSubmittedQuestionId] =
    useState<ProfileStepIds | null>(null);

  const [currentQuestionIndex, setCurrentQuestionIndex] =
    useState(initialQuestionIndex);
  const { updateProfile, completeOnboarding } = useRecommendationProfile();

  const currentQuestionId =
    questions[currentQuestionIndex]?.id ?? ProfileStepIds.PresentationWelcome;

  const visibleQuestionIds = useMemo(() => {
    const ids: ProfileStepIds[] = [];
    for (let i = 0; i <= currentQuestionIndex; i++) {
      const question = questions[i];
      if (isQuestionVisible(question, answers)) {
        ids.push(question.id);
      }
    }
    return ids;
  }, [questions, currentQuestionIndex, answers]);

  const nextVisibleQuestion = findNextVisibleQuestion(
    currentQuestionIndex,
    questions,
    answers
  );
  const canGoBack = currentQuestionIndex > 1;

  const canGoNext = isCurrentStepValid && submittedQuestionId === null;

  const isLastQuestion = nextVisibleQuestion === null;

  const setCurrentStepValid = (isValid: boolean) => {
    setIsCurrentStepValid(isValid);
  };

  const setQuizAnswer = <K extends keyof QuizAnswers>(
    field: K,
    value: QuizAnswers[K]
  ) => {
    setAnswers((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const submit = () => {
    const nextVisibleQuestion = findNextVisibleQuestion(
      currentQuestionIndex,
      questions,
      answers
    );

    const isLastQuestion = nextVisibleQuestion === null;

    const nextStep = isLastQuestion
      ? ProfileStepIds.PresentationFinished
      : nextVisibleQuestion.question.id;

    const payload = quizAnswersToProfilePayload(profile.id, answers, nextStep);

    setSubmittedQuestionId(currentQuestionId);

    if (!isLastQuestion) {
      updateProfile({ profile: payload });
    } else {
      completeOnboarding({ profile: payload });
      setIsQuizComplete(true);
    }
  };

  const goNext = () => {
    const nextVisibleQuestion = findNextVisibleQuestion(
      currentQuestionIndex,
      questions,
      answers
    );

    if (nextVisibleQuestion) {
      setCurrentQuestionIndex(nextVisibleQuestion.index);
      setIsCurrentStepValid(false);
      setSubmittedQuestionId(null);
    }
  };

  const goBack = () => {
    if (currentQuestionIndex > 0) {
      for (let i = currentQuestionIndex - 1; i >= 0; i--) {
        if (isQuestionVisible(questions[i], answers)) {
          setCurrentQuestionIndex(i);
          setIsCurrentStepValid(false);
          setSubmittedQuestionId(null);
          return;
        }
      }
    }
  };

  return (
    <ProfileQuizContext.Provider
      value={{
        answers,
        setQuizAnswer,
        currentQuestionId,
        visibleQuestionIds,
        submittedQuestionId,
        submit,
        goNext,
        goBack,
        canGoBack,
        canGoNext,
        isLastQuestion,
        isQuizComplete,
        setCurrentStepValid,
      }}
    >
      {children}
    </ProfileQuizContext.Provider>
  );
};
